import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Rocket, Users, Code, MessageCircle, TrendingUp, Star } from 'lucide-react';

export default function LandingPage() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentUser) {
      navigate('/feed');
    }
  }, [currentUser, navigate]);

  const features = [
    {
      icon: <Code className="w-12 h-12" />,
      title: 'Share Projects',
      description: 'Upload and showcase your amazing CS projects with the community'
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: 'Collaborate',
      description: 'Find teammates and collaborate on exciting new projects'
    },
    {
      icon: <MessageCircle className="w-12 h-12" />,
      title: 'Real-time Chat',
      description: 'Connect with fellow students through instant messaging'
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Track Progress',
      description: 'Monitor your projects from idea stage to completion'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-cyan-50 to-blue-50" data-testid="landing-page">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-8">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-sky-400 via-cyan-500 to-blue-500 flex items-center justify-center shadow-2xl">
                <Rocket className="w-14 h-14 text-white" />
              </div>
            </div>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6">
              <span className="gradient-text">Coder Trade</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The ultimate platform for Computer Science students to share, discover, and collaborate on innovative projects
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white font-semibold px-8 py-6 text-lg rounded-full shadow-lg"
                onClick={() => navigate('/auth')}
                data-testid="get-started-button"
              >
                Get Started
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-2 border-sky-500 text-sky-600 hover:bg-sky-50 font-semibold px-8 py-6 text-lg rounded-full"
                onClick={() => navigate('/auth')}
                data-testid="learn-more-button"
              >
                Learn More
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Floating elements */}
        <div className="absolute top-20 left-10 opacity-20">
          <div className="w-32 h-32 rounded-full bg-gradient-to-br from-sky-400 to-cyan-500 blur-3xl"></div>
        </div>
        <div className="absolute bottom-20 right-10 opacity-20">
          <div className="w-40 h-40 rounded-full bg-gradient-to-br from-blue-400 to-sky-500 blur-3xl"></div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-bold mb-4">
            <span className="gradient-text">Why Coder Trade?</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Everything you need to showcase your work and connect with brilliant minds
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="glass p-8 rounded-3xl hover:shadow-xl transition-shadow duration-300"
              data-testid={`feature-card-${index}`}
            >
              <div className="text-sky-500 mb-4">{feature.icon}</div>
              <h3 className="text-xl font-bold mb-3 text-gray-800">{feature.title}</h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Stats Section */}
      <div className="bg-gradient-to-r from-sky-500 to-cyan-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">1000+</div>
              <div className="text-xl opacity-90">Projects Shared</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">500+</div>
              <div className="text-xl opacity-90">Active Students</div>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">100+</div>
              <div className="text-xl opacity-90">Colleges</div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="glass p-12 rounded-3xl text-center"
        >
          <Star className="w-16 h-16 text-sky-500 mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">
            <span className="gradient-text">Ready to Get Started?</span>
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Join thousands of CS students sharing their innovative projects and building amazing connections
          </p>
          <Button
            size="lg"
            className="bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 text-white font-semibold px-10 py-6 text-lg rounded-full shadow-lg"
            onClick={() => navigate('/auth')}
            data-testid="cta-button"
          >
            Join Coder Trade Now
          </Button>
        </motion.div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-gray-600">
          <p>© 2025 Coder Trade. Built for CS students, by CS students.</p>
        </div>
      </footer>
    </div>
  );
}